import { Skeleton, Stack, Typography } from "@mui/material";
import { Resources, useResource } from "Translations/Resources";
import { ImageUpload } from "Components/Shared/ImageUpload/ImageUpload";
import { useUploadCampaignImageMutation } from "Api/Mutations/Admin/useUploadCampaignImageMutation";
import { useDeleteCampaignImageMutation } from "Api/Mutations/Admin/useDeleteCampaignImageMutation";
import { useCampaignByIdQuery } from "Api/Queries/Admin/useCampaignByIdQuery";
import {
  useAdminCampaignImageQuery,
  useRevokeBlobUrlOnUnmount,
} from "Api/Queries/Admin/useAdminCampaignImageQuery";
import {
  CampaignImageType,
  type AdminCampaignDto,
  type ApiProblemDetails,
} from "Infrastructure/Api/Api";
import { toast } from "sonner";

const RESOURCES = Resources.Admin.Campaigns.Form;

const getImageFileName = (
  campaign: AdminCampaignDto | undefined,
  imageType: CampaignImageType,
) => {
  if (!campaign) {
    return undefined;
  }
  const map: Record<CampaignImageType, string | null | undefined> = {
    [CampaignImageType.Logo]: campaign.logoFileName,
    [CampaignImageType.Landing]: campaign.landingImageFileName,
    [CampaignImageType.ThankYou]: campaign.thankYouImageFileName,
    [CampaignImageType.Icon]: campaign.iconFileName,
  };
  return map[imageType];
};

// Backend CampaignErrors share ErrorCode.Invalid; the discriminating identifier
// (invalidFileType / fileSizeExceeded / fileContentMismatch) lives in detail.message.
// UploadError translation keys mirror those identifiers for direct lookup.
const getUploadErrorResource = (error: unknown) => {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return RESOURCES.ImageUploadError;
  }
  const errorData = (error as { data: ApiProblemDetails }).data;
  const fileErrors = errorData?.errors?.File ?? errorData?.errors?.file ?? [];
  for (const detail of fileErrors) {
    const key = detail?.message as
      | keyof typeof RESOURCES.UploadError
      | undefined;
    if (key && key in RESOURCES.UploadError) {
      return RESOURCES.UploadError[key];
    }
  }
  return RESOURCES.ImageUploadError;
};

type Props = {
  campaignId: number;
  imageType: CampaignImageType;
  label: string;
  placeholder?: React.ReactNode;
};

export const CampaignImageUpload = ({
  campaignId,
  imageType,
  label,
  placeholder,
}: Props) => {
  const { t } = useResource();
  const {
    data: campaign,
    isLoading: isCampaignLoading,
    dataUpdatedAt,
  } = useCampaignByIdQuery(campaignId);

  const fileName = getImageFileName(campaign, imageType);
  const { data: imageUrl, isFetching: isImageFetching } =
    useAdminCampaignImageQuery({
      campaignId,
      imageType,
      fileName,
      cacheKey: dataUpdatedAt,
    });

  useRevokeBlobUrlOnUnmount(imageUrl);

  const isLoadingImage = isCampaignLoading || isImageFetching;

  const { mutate: uploadImage, isPending: isUploading } =
    useUploadCampaignImageMutation({
      onSuccess: () => toast.success(t(RESOURCES.ImageUploadSuccess)),
      onError: (error) => toast.error(t(getUploadErrorResource(error))),
    });

  const { mutate: deleteImage, isPending: isDeleting } =
    useDeleteCampaignImageMutation({
      onSuccess: () => toast.success(t(RESOURCES.ImageDeleteSuccess)),
      onError: () => toast.error(t(RESOURCES.ImageDeleteError)),
    });

  if (isLoadingImage) {
    return (
      <Stack gap={1}>
        <Typography variant="body1">{label}</Typography>
        <Skeleton variant="rounded" height={80} sx={{ borderRadius: 2 }} />
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      <Typography variant="body1">{label}</Typography>
      <ImageUpload
        imageUrl={imageUrl ?? undefined}
        isPending={isUploading || isDeleting}
        onUpload={(file) => uploadImage({ campaignId, imageType, file })}
        onDelete={() => deleteImage({ campaignId, imageType })}
        placeholder={placeholder}
      />
    </Stack>
  );
};
